/**
 * Task format from API: GET /Portal/GetTasks
 * Returns an array of tasks for the patient.
 *
 * Each task has:
 * - taskID: number
 * - taskType: string — one of:
 *   - STMT: Pay statement / balance
 *   - NPH: New patient health (demographics / health history)
 *   - AANPH: Auto accident new patient health
 *   - CH: Case history (complaint flow)
 *   - AACH: Auto accident case history
 *   - INS: Insurance information
 *   - FORM: Form to complete/sign — info JSON matches FacilityFormInfo (FacilityFormID, Action, FormName, Data map)
 *   - FV: File viewer / review (file IDs from GetFvTaskAction)
 *   - MSG: Message to read (info holds message id)
 * - info: string — JSON. For FORM: { FormName, FacilityFormID, Data?: { PatientName } }. For INS: { Description }. For MSG: message id in response from getSpecificTask.
 * - patientID: string
 * - isHistory: boolean — true when the task belongs in "Complete Information" on the dashboard (PatientPortalTask).
 *
 * Dashboard groups tasks into three combined action items:
 * 1. Pay Statement/Balance — STMT (one card; opens first statement).
 * 2. Complete Information — isHistory true from API (one card; opens first such task).
 * 3. Complete and Sign Forms — FORM (one card; opens first form; forms link to each other in handler).
 * 4. Insurance Information — INS (one card; demographics + insurance only, no history).
 * MSG and other types are shown as separate cards (e.g. "New Message").
 */

export interface TaskItem {
  taskID: number;
  taskType: string;
  info: string;
  patientID: string;
  /** When true, task belongs in the dashboard "Complete Information" group (PatientPortalTask.isHistory). */
  isHistory: boolean;
}

/** Task types that belong to "Pay Statement/Balance" */
export const STATEMENT_TASK_TYPES = ["STMT"] as const;

/** Task types that belong to "Complete and Sign Forms" */
export const FORM_TASK_TYPES = ["FORM"] as const;

export const FV_TASK_TYPES = ["FV"] as const;

/** Insurance-only request (staff ticked only Insurance Information when sending the portal link). */
export const INSURANCE_TASK_TYPES = ["INS"] as const;

export function isStatementTask(type: string): boolean {
  const t = (type ?? "").toUpperCase();
  return STATEMENT_TASK_TYPES.includes(t as (typeof STATEMENT_TASK_TYPES)[number]);
}

export function isInfoTask(task: Pick<TaskItem, "isHistory">): boolean {
  return task.isHistory === true;
}

/** Task in store from the dashboard with a portal task id and isHistory from the API. */
export function isActiveInfoTaskItem(item: unknown): item is TaskItem {
  return (
    typeof item === "object" &&
    item !== null &&
    typeof (item as TaskItem).taskID === "number" &&
    isInfoTask(item as TaskItem)
  );
}

export function isFormTask(type: string): boolean {
  const t = (type ?? "").toUpperCase();
  return FORM_TASK_TYPES.includes(t as (typeof FORM_TASK_TYPES)[number]);
}

export function isFvTask(type: string): boolean {
  const t = (type ?? "").toUpperCase();
  return FV_TASK_TYPES.includes(t as (typeof FV_TASK_TYPES)[number]);
}

/**
 * INS task that is not already in "Complete Information". The API marks INS isHistory = false
 * (PatientPortalTaskType), so it gets its own dashboard card and an insurance-only wizard.
 */
export function isInsuranceTask(task: Pick<TaskItem, "taskType" | "isHistory">): boolean {
  const t = (task.taskType ?? "").toUpperCase();
  return (
    !isInfoTask(task) &&
    INSURANCE_TASK_TYPES.includes(t as (typeof INSURANCE_TASK_TYPES)[number])
  );
}
